import { useTranslation } from 'react-i18next'

import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import type { BulkWithdrawMenu } from '~/pages/SpotBidding/hooks/useBulkWithdrawControl'

/** ⋯ メニュー項目「入札中の札を一括で取下」 の表示。 */
export type BulkWithdrawMenuView = {
  disabled: boolean
  /**
   * 項目の補足の文 (説明行)。 通常入札 / ブロック入札の一覧を取得できていない間は、 その理由の文に置き換わる
   * (CSV ダウンロードが手数料単価の取得失敗の理由を出すのと同じ)。 それ以外は固定 (design `spot.html` の説明行)。
   */
  description: string
  /** 項目のツールチップ。 押せる時は絞り込み条件に合う件数、 押せない時はその理由。 */
  title: string | undefined
}

/**
 * 通常入札タブ・ブロック入札タブの ⋯ メニューで共通の、 一括取下の項目の表示を決める。
 *
 * 押せない理由の示し方は spec AC-BSP-116 の表と `conventions.md`「操作できない時のボタンの扱い」 に従う。
 * - 一覧を取得できていない間 (待てば操作できる前提を満たさない。 2): 説明行を理由の文に置き換え (画面上の文)、
 *   title にも同じ文を付ける。 文は `serverError.detailUnresolved`
 * - 読み取り専用・入札締切後・対象が 0 件 (操作できる状態に戻らない・対象が無い。 3): 説明行は固定で、
 *   理由は title (ツールチップ) だけ (design `spot.html` の `spotBulkWithdrawAC116`)
 */
export const useBulkWithdrawMenuView = (
  menu: BulkWithdrawMenu | undefined,
): BulkWithdrawMenuView | undefined => {
  const { t } = useTranslation()
  const { readonlyReason } = useWorkflowLockReasons()
  if (menu === undefined) return undefined
  const description = t('page:biddingSpot.bulkWithdraw.menuDesc')
  const disabledWith = (title: string | undefined): BulkWithdrawMenuView => ({
    disabled: true,
    description,
    title,
  })
  const disabledWithOnScreenReason = (
    reason: string,
  ): BulkWithdrawMenuView => ({
    disabled: true,
    description: reason,
    title: reason,
  })
  switch (menu.state) {
    case 'active':
      return {
        disabled: false,
        description,
        title: t('page:biddingSpot.bulkWithdraw.menuTooltip', {
          normal: menu.normalCount,
          block: menu.blockCount,
        }),
      }
    case 'readonly':
      return disabledWith(readonlyReason)
    case 'deadline':
      return disabledWith(t('page:biddingSpot.bulkWithdraw.deadlineReason'))
    case 'spotUnresolved':
      return disabledWithOnScreenReason(
        t('page:biddingSpot.serverError.detailUnresolved'),
      )
    case 'blockUnresolved':
      return disabledWithOnScreenReason(
        t('page:biddingBlock.serverError.detailUnresolved'),
      )
    case 'empty':
      return disabledWith(t('page:biddingSpot.bulkWithdraw.emptyReason'))
  }
}
